import React, { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';

/**
 * Format HS code as segments: "8536 20 20 90" — first 4 digits, then pairs.
 * Returns array of { text, level } for hierarchy color coding.
 */
export function formatHSCode(code) {
  if (!code || code.length < 4) return [{ text: code || '', level: 0 }];
  const segments = [];
  segments.push({ text: code.slice(0, 4), level: 0 }); // Chapter + heading
  for (let i = 4; i < code.length; i += 2) {
    segments.push({ text: code.slice(i, i + 2), level: Math.floor((i - 4) / 2) + 1 });
  }
  return segments;
}

/**
 * Copy text to clipboard with fallback for non-HTTPS environments.
 */
export function copyToClipboard(text, onSuccess) {
  const doFallback = () => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      onSuccess?.();
    } catch { /* ignore */ }
    document.body.removeChild(ta);
  };

  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(onSuccess).catch(doFallback);
  } else {
    doFallback();
  }
}

// Colors fade for deeper levels: bold navy -> medium blue -> light blue -> gray
const LEVEL_COLORS = [
  'text-blue-900 dark:text-blue-200', // 4-digit chapter+heading
  'text-blue-700 dark:text-blue-300', // subheading pair 1
  'text-blue-500 dark:text-blue-400', // subheading pair 2
  'text-blue-400 dark:text-blue-500', // national pair
];

/**
 * Reusable HS Code display with hierarchy coloring, diff highlighting, and copy button.
 *
 * Props:
 *   code            - raw HS code string (e.g. "8501320090")
 *   size            - 'sm' (11px) or 'md' (14px), default 'sm'
 *   commonPrefixLen - for grouped mode: number of shared prefix chars (dims common part)
 *   showCopyAlways  - if true, copy button always visible; if false, show on hover
 */
export default function HSCode({ code, size = 'sm', commonPrefixLen = 0, showCopyAlways = false }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e) => {
    e.stopPropagation();
    copyToClipboard(code, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [code]);

  if (!code) return null;

  const segments = formatHSCode(code);
  const sizeClasses = size === 'lg'
    ? 'font-mono text-[20px] leading-[1.05] font-bold tracking-tight tabular-nums'
    : size === 'md'
      ? 'font-mono text-sm font-bold'
      : 'font-mono text-[11px] font-semibold';
  const iconSize = size === 'lg' ? 'w-4 h-4' : size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3';
  const btnPad = size === 'lg' ? 'p-1' : size === 'md' ? 'p-1' : 'p-0.5';

  // Build character-level position tracking for diff highlighting
  let charIndex = 0;

  return (
    <span className="inline-flex items-center gap-1.5 group/hscode">
      {/* Rendered segments with hierarchy colors */}
      <span className={sizeClasses}>
        {segments.map((seg, i) => {
          const segStart = charIndex;
          charIndex += seg.text.length;
          const color = LEVEL_COLORS[Math.min(seg.level, LEVEL_COLORS.length - 1)];

          // Diff highlighting: dim shared prefix, bold differing part
          const inPrefix = commonPrefixLen > 0 && segStart + seg.text.length <= commonPrefixLen;
          const isPartial = commonPrefixLen > 0 && segStart < commonPrefixLen && segStart + seg.text.length > commonPrefixLen;

          if (isPartial) {
            const splitAt = commonPrefixLen - segStart;
            return (
              <React.Fragment key={i}>
                {i > 0 && <span className="text-text-disabled mx-px">{'\u00B7'}</span>}
                <span className={`${color} opacity-50`}>{seg.text.slice(0, splitAt)}</span>
                <span className={`${color} font-bold`}>{seg.text.slice(splitAt)}</span>
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-gray-300 mx-px">{'\u00B7'}</span>}
              <span className={`${color} ${inPrefix ? 'opacity-50' : commonPrefixLen > 0 ? 'font-bold' : ''}`}>
                {seg.text}
              </span>
            </React.Fragment>
          );
        })}
      </span>

      {/* Copy button */}
      {copied ? (
        <Check className={`${iconSize} text-green-500 dark:text-green-400`} />
      ) : (
        <button
          onClick={handleCopy}
          className={`${btnPad} rounded hover:bg-blue-100 dark:hover:bg-blue-950/50 text-text-disabled hover:text-blue-600 dark:hover:text-blue-400
                     transition-colors
                     ${showCopyAlways ? '' : 'opacity-0 group-hover/hscode:opacity-100'}`}
          title="Копировать код"
        >
          <Copy className={iconSize} />
        </button>
      )}
    </span>
  );
}
